import { useEffect, useState } from 'react'
import { Eye, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/members/pagination'
import { useInvoices, useMarkDraftInvoicesAsSent } from '@/hooks/use-billing'
import { useUtilityTypes } from '@/hooks/use-utilities'
import { getApiErrorMessage } from '@/lib/api'
import { InvoiceDetailModal } from './invoice-detail-modal'
import { AmountCell, formatDate, InvoiceTypeBadge, StatusBadge } from './invoice-display-shared'
import type { Invoice, InvoiceStatus } from '@riverpark/types'

const SELECT_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

const STATUS_OPTIONS: InvoiceStatus[] = ['draft', 'sent', 'paid', 'overdue', 'voided']
const PAGE_SIZE = 20

export function InvoiceListTab() {
  const [status, setStatus] = useState('')
  const [utilityTypeId, setUtilityTypeId] = useState('')
  const [page, setPage] = useState(1)
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null)
  const [markSentResult, setMarkSentResult] = useState<string | null>(null)
  const [markSentError, setMarkSentError] = useState<string | null>(null)

  useEffect(() => setPage(1), [status, utilityTypeId])

  const { data: utilityData } = useUtilityTypes()
  const utilityTypes = utilityData?.data ?? []

  const { data, isLoading } = useInvoices({
    page,
    limit: PAGE_SIZE,
    excludeInvoiceType: 'credit_note',
    ...(status && { status: status as InvoiceStatus }),
    ...(utilityTypeId && { utilityTypeId }),
  })
  const invoices = data?.data ?? []
  const meta = data?.meta ?? { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 }

  const markDraftsAsSent = useMarkDraftInvoicesAsSent()

  async function handleMarkAllAsSent() {
    setMarkSentResult(null)
    setMarkSentError(null)
    const confirmed = confirm(
      `Mark all ${meta.total} draft invoice(s) as sent? This will send an SMS to each affected member.`,
    )
    if (!confirmed) return
    try {
      const { data: result } = await markDraftsAsSent.mutateAsync({
        ...(utilityTypeId && { utilityTypeId }),
      })
      setMarkSentResult(`${result.updated} invoice(s) marked as sent.`)
    } catch (err) {
      setMarkSentError(getApiErrorMessage(err, 'Failed to mark invoices as sent'))
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row">
          <select
            className={`${SELECT_CLS} sm:max-w-[180px]`}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s[0].toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
          <select
            className={`${SELECT_CLS} sm:max-w-[220px]`}
            value={utilityTypeId}
            onChange={(e) => setUtilityTypeId(e.target.value)}
          >
            <option value="">All utility types</option>
            {utilityTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        {status === 'draft' && meta.total > 0 && (
          <Button
            variant="secondary"
            size="sm"
            loading={markDraftsAsSent.isPending}
            onClick={() => void handleMarkAllAsSent()}
          >
            <Send size={14} className="mr-1.5" />
            Mark all {meta.total} as sent
          </Button>
        )}
      </div>
      {markSentResult && (
        <p className="font-sans text-xs text-primary" role="status">
          {markSentResult}
        </p>
      )}
      {markSentError && (
        <p className="font-sans text-xs text-destructive" role="alert">
          {markSentError}
        </p>
      )}

      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading invoices…</p>
      ) : invoices.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          No invoices match these filters.
        </p>
      ) : (
        <>
          {/* Mobile: cards, action up front */}
          <div className="space-y-2 sm:hidden">
            {invoices.map((inv) => (
              <div key={inv.id} className="rounded border border-border-2 bg-card p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="font-sans text-sm font-medium text-ink">
                      {inv.invoiceNumber}
                    </span>
                    <StatusBadge status={inv.status} />
                    <InvoiceTypeBadge invoiceType={inv.invoiceType} />
                  </div>
                  <Button variant="secondary" size="sm" onClick={() => setActiveInvoice(inv)}>
                    <Eye size={14} className="mr-1.5" />
                    View
                  </Button>
                </div>
                <div className="mt-2 font-sans text-xs text-muted-foreground">
                  <p>
                    {inv.memberName} · Plot {inv.plotNumber}
                  </p>
                  <p>
                    {inv.utilityTypeName ?? '—'} · <AmountCell invoice={inv} /> · Due{' '}
                    {formatDate(inv.dueDate)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop / tablet */}
          <div className="hidden overflow-x-auto rounded border border-border-2 sm:block">
            <table className="w-full font-sans text-sm">
              <thead>
                <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                  <th className="px-4 py-3">Invoice #</th>
                  <th className="px-4 py-3">Member</th>
                  <th className="px-4 py-3">Plot</th>
                  <th className="px-4 py-3">Utility</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-border-2 last:border-0">
                    <td className="px-4 py-3 font-medium text-ink">{inv.invoiceNumber}</td>
                    <td className="px-4 py-3 text-ink">{inv.memberName}</td>
                    <td className="px-4 py-3 text-ink">{inv.plotNumber}</td>
                    <td className="px-4 py-3 text-ink">{inv.utilityTypeName ?? '—'}</td>
                    <td className="px-4 py-3 text-ink">
                      <AmountCell invoice={inv} />
                    </td>
                    <td className="px-4 py-3 text-ink">{formatDate(inv.dueDate)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <StatusBadge status={inv.status} />
                        <InvoiceTypeBadge invoiceType={inv.invoiceType} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="secondary" size="sm" onClick={() => setActiveInvoice(inv)}>
                        <Eye size={14} className="mr-1.5" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {meta.total > 0 && (
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          total={meta.total}
          limit={meta.limit}
          onPageChange={setPage}
          emptyLabel="No invoices"
          className="rounded border border-border-2 bg-card"
        />
      )}

      {activeInvoice && (
        <InvoiceDetailModal invoice={activeInvoice} onClose={() => setActiveInvoice(null)} />
      )}
    </div>
  )
}
